/**
 * NICE 본인인증 콜백(`?data=...`) 해석.
 *
 * 흐름: Join 화면 → NICE checkplus → `/Join/UserJoin/redirect/GET?data=<base64>`
 * (기업회원은 `/Join/OfficeJoin/redirect/GET`). 두 화면이 같은 코드를 복사해 쓰고 있었다.
 *
 * 예전 코드는 이랬다.
 *
 *   const buffer = locationSearch.get('data') || '';
 *   const urlSearchParams = new URLSearchParams(`?${atob(buffer)}`);
 *   let params = Object.fromEntries(urlSearchParams.entries());
 *   setSaveBasicParam(prev => ({ ...prev, userName: decodeURIComponent(params.name) }));
 *
 * 여기서 두 가지가 터졌다.
 *
 * 1) **깨지거나 잘린 base64** 에서 `atob` 가 InvalidCharacterError 를 던진다.
 *    잡는 곳이 없어 가입 화면이 통째로 흰 화면이 됐다(인증을 취소하고 돌아오거나,
 *    뒤로가기로 콜백 주소에 다시 들어오는 경우가 대표적이다).
 * 2) **`data` 가 없으면** 예외 없이 통과하지만 `decodeURIComponent(undefined)` 가
 *    문자열 `"undefined"` 를 돌려줘서, 이름과 생년월일이 `"undefined"` 인 채로
 *    다음 단계까지 진행됐다(그대로 저장될 수 있었다).
 *
 * `normalizeBase64` 의 공백→`+` 복원은 **예방 목적**이다. `URLSearchParams.get()` 은
 * 값을 application/x-www-form-urlencoded 로 읽어 `+` 를 공백으로 바꾸는데, base64
 * 알파벳에는 `+` 가 들어 있다. 다만 지금 오는 페이로드(퍼센트 인코딩된 이름 + 생년월일)로는
 * `+` 가 나오지 않는 것을 확인했다 — 문자 집합이 좁아서다. 서버가 필드를 늘리거나
 * 인코딩 방식을 바꾸면 바로 문제가 되는 자리라 미리 막아 둔다.
 *
 * 그래서 파싱을 여기로 모으고, **해석에 성공했고 값이 형식에 맞을 때만** 결과를 준다.
 * 실패는 null 로 알리고 화면이 사용자를 인증 시작 지점으로 되돌린다.
 */

export type NiceAuthResult = {
  /** 실명 */
  userName: string;
  /** YYYYMMDD */
  birthDate: string;
};

/**
 * base64 문자열 정리.
 *  - 공백 → `+`  : URLSearchParams 가 바꿔 놓은 것을 되돌린다
 *  - `-`/`_`     : base64url 로 온 경우를 표준 base64 로 맞춘다
 *  - `=` 패딩    : 길이가 4의 배수가 아니면 atob 가 실패하므로 채운다
 */
export const normalizeBase64 = (raw: string): string => {
  const body = raw.replace(/\s/g, '+').replace(/-/g, '+').replace(/_/g, '/');
  const remainder = body.length % 4;
  return remainder === 0 ? body : body + '='.repeat(4 - remainder);
};

/**
 * 이미 디코드된 값에 다시 decodeURIComponent 를 걸어도 안전하게.
 *
 * URLSearchParams 는 값을 이미 퍼센트 디코드해서 준다. 그런데 기존 코드가 그 위에
 * decodeURIComponent 를 한 번 더 걸고 있었고, 운영에서 그 상태로 동작해 왔다
 * (NICE 쪽이 두 번 인코딩해 보내는 것으로 보인다). 동작을 바꾸지 않으려고 유지하되,
 * 값에 리터럴 `%` 가 있으면 URIError 가 나므로 그때는 원래 값을 쓴다.
 */
const decodeTwiceSafely = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
};

/** YYYYMMDD 8자리인지 (달·일의 범위까지 본다) */
export const isBirthDate = (value: string): boolean => {
  if (!/^\d{8}$/.test(value)) {
    return false;
  }
  const month = Number(value.slice(4, 6));
  const day = Number(value.slice(6, 8));
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
};

/**
 * 콜백 URL 의 `data` 값을 해석한다.
 *
 * @param encoded `new URLSearchParams(location.search).get('data')` 결과
 * @returns 이름·생년월일을 모두 얻었을 때만 결과, 아니면 null
 */
export const parseNiceAuthData = (encoded: string | null | undefined): NiceAuthResult | null => {
  if (encoded === null || encoded === undefined || encoded === '') {
    return null;
  }

  let decoded: string;
  try {
    decoded = window.atob(normalizeBase64(encoded));
  } catch (e) {
    // 잘렸거나 base64 가 아니다 — 인증을 다시 받아야 한다
    console.warn('[본인인증] data 파라미터를 해석하지 못했습니다.', e);
    return null;
  }

  const params = new URLSearchParams(`?${decoded}`);
  const rawName = params.get('name');
  const rawBirth = params.get('birthdate');
  if (rawName === null || rawBirth === null) {
    console.warn('[본인인증] 응답에 name / birthdate 가 없습니다.');
    return null;
  }

  const userName = decodeTwiceSafely(rawName).trim();
  const birthDate = decodeTwiceSafely(rawBirth).trim();

  if (userName === '' || !isBirthDate(birthDate)) {
    console.warn('[본인인증] 이름 또는 생년월일 형식이 올바르지 않습니다.');
    return null;
  }

  return { userName, birthDate };
};
